/** Settings: Controls (every action's key, click to rebind), Graphics, Audio. */

import { For, Show, createSignal, onCleanup, type JSX } from "solid-js";
import * as keys from "../ui/bindings";
import { settings, setSetting, type Settings as S } from "../ui/settings";
import { TRACKS } from "../audio/music";

type Page = "controls" | "graphics" | "audio" | "credits";

export function Settings(): JSX.Element {
  const [page, setPage] = createSignal<Page>("controls");
  return (
    <>
      <div class="start-replays-head">
        <h1>Settings</h1>
        <div class="segmented" role="radiogroup" aria-label="Settings">
          <For each={["controls", "graphics", "audio", "credits"] as Page[]}>
            {(p) => (
              <button role="radio" aria-checked={page() === p} onClick={() => setPage(p)}>
                {p[0].toUpperCase() + p.slice(1)}
              </button>
            )}
          </For>
        </div>
      </div>
      <div class="settings-page">
        <Show when={page() === "controls"}>
          <Controls />
        </Show>
        <Show when={page() === "graphics"}>
          <Choice label="Render resolution" key="resolution" options={[[1, "Native"], [0.75, "75%"], [0.5, "50%"]]} />
          <Choice label="Terrain detail" key="terrainDetail" options={[[true, "High"], [false, "Low"]]} />
          <Choice label="Effects" key="effects" options={[[true, "On"], [false, "Off"]]} />
        </Show>
        <Show when={page() === "audio"}>
          <Slider label="Master volume" key="master" />
          <Slider label="Effects" key="effectsVolume" />
          <Choice label="Music" key="musicOn" options={[[true, "On"], [false, "Off"]]} />
          <Slider label="Music volume" key="music" />
        </Show>
        <Show when={page() === "credits"}>
          <Credits />
        </Show>
      </div>
    </>
  );
}

/** The music's credits: every shipped track, its artist and licence (the
 *  CC-BY ones marked as changed, as their licence asks: they were
 *  loudness normalized); the CC0 ones thanked anyway. */
function Credits(): JSX.Element {
  const LICENSES: Record<string, string> = {
    "CC BY 4.0": "https://creativecommons.org/licenses/by/4.0/",
    "CC BY 3.0": "https://creativecommons.org/licenses/by/3.0/",
    CC0: "https://creativecommons.org/publicdomain/zero/1.0/",
  };
  const by = TRACKS.filter((t) => t.license !== "CC0");
  const free = TRACKS.filter((t) => t.license === "CC0");
  return (
    <div class="credits">
      <h2>Music</h2>
      <ul>
        <For each={by}>
          {(t) => (
            <li>
              "{t.title}" (loudness normalized) by{" "}
              {t.artistUrl ? (
                <a href={t.artistUrl} target="_blank" rel="noreferrer">
                  {t.artist}
                </a>
              ) : (
                t.artist
              )}
              , licensed under{" "}
              <a href={LICENSES[t.license]} target="_blank" rel="noreferrer">
                {t.license}
              </a>
              . <a href={t.source} target="_blank" rel="noreferrer">Source</a>
            </li>
          )}
        </For>
      </ul>
      <p class="muted">With thanks, public domain (CC0), via OpenGameArt.org:</p>
      <ul>
        <For each={free}>
          {(t) => (
            <li>
              "{t.title}" by {t.artist}.{" "}
              <a href={t.source} target="_blank" rel="noreferrer">
                Source
              </a>
            </li>
          )}
        </For>
      </ul>
    </div>
  );
}

/** Every bindable action by group, its key a button: click, then press a key. */
function Controls(): JSX.Element {
  // Bindings live outside Solid: a version bump re-reads them.
  const [version, setVersion] = createSignal(0);
  onCleanup(keys.onBindingsChange(() => setVersion((v) => v + 1)));
  const [waiting, setWaiting] = createSignal<string | null>(null);
  // A capture still waiting when the page closes stops listening.
  let pending: ((e: KeyboardEvent) => void) | null = null;
  onCleanup(() => pending && window.removeEventListener("keydown", pending, true));
  const groups = () => {
    const out = new Map<string, keys.Action[]>();
    for (const a of keys.ACTIONS) out.set(a.group, [...(out.get(a.group) ?? []), a]);
    return [...out];
  };
  const capture = (id: string) => {
    if (pending) window.removeEventListener("keydown", pending, true);
    setWaiting(id);
    const onKey = (e: KeyboardEvent) => {
      if (["Control", "Alt", "Shift", "Meta"].includes(e.key)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      window.removeEventListener("keydown", onKey, true);
      pending = null;
      setWaiting(null);
      if (e.code !== "Escape") keys.setBinding(id, { code: e.code, ctrl: e.ctrlKey, alt: e.altKey });
    };
    pending = onKey;
    window.addEventListener("keydown", onKey, true);
  };
  return (
    <>
      <For each={groups()}>
        {([group, actions]) => (
          <>
            <h2>{group}</h2>
            <For each={actions}>
              {(a) => {
                const clash = () => (version(), keys.conflicts(a.id));
                return (
                  <div class="setting-row">
                    <span>{a.label}</span>
                    <button
                      class="bind"
                      classList={{ clash: clash().length > 0, waiting: waiting() === a.id }}
                      data-tip={clash().length ? `Also bound to: ${clash().map((c) => c.label).join(", ")}` : undefined}
                      onClick={() => capture(a.id)}
                    >
                      {waiting() === a.id ? "Press a key…" : (version(), keys.keyName(keys.binding(a.id)))}
                    </button>
                  </div>
                );
              }}
            </For>
          </>
        )}
      </For>
      <h2>Fixed</h2>
      <For each={keys.FIXED}>
        {([k, what]) => (
          <div class="setting-row">
            <span>{what}</span>
            <span class="fixed-key">{k}</span>
          </div>
        )}
      </For>
      <div class="setting-actions">
        <button onClick={() => keys.resetBindings()}>Reset to defaults</button>
      </div>
    </>
  );
}

function Choice<K extends keyof S>(props: { label: string; key: K; options: [S[K], string][] }): JSX.Element {
  return (
    <div class="setting-row">
      <span>{props.label}</span>
      <div class="options" role="radiogroup" aria-label={props.label}>
        <For each={props.options}>
          {([v, text]) => (
            <button role="radio" aria-checked={settings[props.key] === v} onClick={() => setSetting(props.key, v)}>
              {text}
            </button>
          )}
        </For>
      </div>
    </div>
  );
}

function Slider(props: { label: string; key: "master" | "effectsVolume" | "music" }): JSX.Element {
  return (
    <div class="setting-row">
      <span>{props.label}</span>
      <span class="slider">
        <input type="range" min="0" max="100" value={settings[props.key]} onInput={(e) => setSetting(props.key, Number(e.currentTarget.value))} />
        <span class="slider-value">{settings[props.key]}</span>
      </span>
    </div>
  );
}
